import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { HttpService } from '../../../core/services/http/http-service';
import { UserHttpService } from '../../../core/services/http/user-http-service';
import { Order, OrderContent } from '../../../core/models/order';
import { User } from '../../../core/models/user';
import { forkJoin, map, Observable } from 'rxjs';
import { Stock } from '../../../core/models/stock';

@Component({
  selector: 'app-action-dialog',
  templateUrl: './action-dialog.html',
  styleUrl: './action-dialog.css',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class ActionDialog implements OnChanges {
  @Input({ required: true })
  order: Order | null = null;

  @Output()
  onClose: EventEmitter<void> = new EventEmitter<void>();

  protected createdBy$: Observable<User> | null = null;
  protected lastUpdatedBy$: Observable<User | 'system'> | null = null;
  protected stocks$: Observable<Array<StockWithQuantity>> | null = null;
  protected hasStock = true;

  constructor(
    private readonly httpService: HttpService,
    private readonly userHttpService: UserHttpService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['order']) {
      this.createdBy$ = this.userHttpService.findUserById(this.order?.userId || '');
      this.lastUpdatedBy$ = this.getLastUpdatedBy();
      const contentMap = new Map<number, OrderContent>();
      this.hasStock = (this.order?.contents || []).length !== 0;
      for (const orderContent of this.order?.contents || []) {
        contentMap.set(orderContent.stockId, orderContent);
      }
      this.stocks$ = forkJoin(
        this.order?.contents?.map((stock) => {
          return this.httpService.getStockById(stock.stockId);
        }) ?? [],
      ).pipe(
        map((e) => {
          return e.map((stock) => {
            return {
              ...stock,
              quantity: contentMap.get(stock.id)?.quantity || 0,
            };
          });
        }),
      );
    }
  }

  approve() {
    this.httpService.updateOrder({ id: this.order?.id!, status: 'APPROVED' }).subscribe({
      complete: () => {
        this.onClose.emit();
      },
    });
  }

  reject() {
    this.httpService.updateOrder({ id: this.order?.id!, status: 'REJECTED' }).subscribe({
      complete: () => {
        this.onClose.emit();
      },
    });
  }

  getLastUpdatedByDeconstructed(lastUpdatedBy: User | 'system') {
    if (lastUpdatedBy === 'system') {
      return 'system';
    } else {
      return `${lastUpdatedBy.name} (${lastUpdatedBy.email})`;
    }
  }

  calculateTotalPrice(stocks: Array<StockWithQuantity> | null): number {
    return stocks?.reduce((total, stock) => total + stock.price * stock.quantity, 0) || 0;
  }

  closeModal(triggerRefresh: boolean) {
    if (triggerRefresh) {
      this.onClose.emit();
    } else {
      this.order = null;
    }
  }

  private getLastUpdatedBy(): Observable<User | 'system'> {
    if (this.order?.updatedBy === 'system') {
      return new Observable<User | 'system'>((observer) => {
        observer.next('system');
        observer.complete();
      });
    }

    return this.userHttpService.findUserById(this.order?.updatedBy || '');
  }
}

interface StockWithQuantity extends Stock {
  quantity: number;
}
